import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Advice } from '@Sage/hoi-poi-ui';

import { AGENDA } from 'constants/Entities';
import { EntityChip, EntityChipRow } from 'containers/components/chips';
import SelectFuzzy from 'containers/components/Fields/SelectFuzzy';
import FieldWrapper from 'containers/components/Fields/FieldWrapper';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const propTypes = {
    changeField: PropTypes.func,
    data: PropTypes.object,
    errors: PropTypes.object,
    field: PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        hint: PropTypes.string,
        mandatory: PropTypes.bool,
        readOnly: PropTypes.bool,
        description: PropTypes.string,
        labelMode: PropTypes.string,
        isBulkAction: PropTypes.bool,
        inputAttrs: PropTypes.shape({
            customOnChange: PropTypes.func,
            chipWithLabel: PropTypes.bool,
            getChipProps: PropTypes.func,
            shouldRenderField: PropTypes.func,
            list: PropTypes.string,
            nombre: PropTypes.string,
        }),
    }),
    firstErrorField: PropTypes.bool,
    minSelection: PropTypes.number,
};

const UsersAttendeesField = memo(
    ({ changeField, data, errors, field, firstErrorField, minSelection }) => {
        const {
            description,
            hint,
            isBulkAction,
            inputAttrs,
            label,
            labelMode = 'horizontal',
            mandatory,
            readOnly,
        } = field;

        const { chipWithLabel, customOnChange, getChipProps, shouldRenderField } = inputAttrs;

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleChange = useCallback(
            (item) => {
                if (!item) return;
                const value = data.usersAttendees || [];
                const alreadyExists = value?.find((v) => v && v.value === item.value);

                if (!alreadyExists) {
                    if (!data.isTodoDia) {
                        customOnChange && customOnChange('usersAttendees', [...value, item]);
                    } else {
                        changeField && changeField('usersAttendees')([...value, item]);
                    }
                }
            },
            [changeField, customOnChange, data],
        );

        const handleRemove = useCallback(
            (id) => {
                const value = data.usersAttendees || [];
                const result = value.filter((v) => v && id !== v.value);
                if (!data.isTodoDia) {
                    customOnChange && customOnChange('usersAttendees', result);
                } else {
                    changeField && changeField('usersAttendees')(result);
                }
            },
            [changeField, customOnChange, data],
        );

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const value = useMemo(() => data.usersAttendees || [], [data.usersAttendees]);
        const isValidValue = value.length > 0;

        const adviceContent = useMemo(() => {
            if (data?.isTodoDia) return null;

            const usersAttendees = data?.usersAttendees || [];
            const hasIsAvailable = [];
            const unavailableAttendees = usersAttendees.filter((current) => {
                if (current.hasOwnProperty('isAvailable') && !current.hasOwnProperty('Status')) {
                    hasIsAvailable.push(current);
                }
                return !current.Status && current.isAvailable === false;
            });

            if (
                unavailableAttendees.length > 0 &&
                usersAttendees.length === unavailableAttendees.length
            ) {
                return {
                    content: getLiteral('label_no_invitees_available'),
                    type: 'error',
                };
            } else if (
                hasIsAvailable.length > 0 &&
                unavailableAttendees.length === 0 &&
                usersAttendees.length > 0
            ) {
                return {
                    content: getLiteral('label_invitees_status_available'),
                    type: 'success',
                };
            } else if (hasIsAvailable.length > 0) {
                return {
                    content: getLiteral('label_invitees_status_busy'),
                    type: 'warning',
                };
            }
            return null;
        }, [data?.isTodoDia, data?.usersAttendees]);

        const renderChips = useMemo(() => {
            let isDismissible = true;
            if (readOnly) isDismissible = false;
            if (minSelection && value.length <= minSelection) isDismissible = false;

            return value.map((v) => {
                if (!v) return null;
                const id = v.value;
                const name = v.label;
                const chipProps = getChipProps ? getChipProps(v) : {};
                // Don't pass color when it's all day
                const { color, ...chipPropsWithoutColor } = chipProps;

                if (chipWithLabel && !data.isTodoDia) {
                    return (
                        <EntityChipRow
                            key={id}
                            entity={inputAttrs.list}
                            id={Number(id)}
                            isDismissible={isDismissible}
                            name={name}
                            onClickDismissible={() => handleRemove(id)}
                            remotePopover
                            labelColor={color}
                            {...chipPropsWithoutColor}
                        />
                    );
                }

                return (
                    <EntityChip
                        key={id}
                        entity={inputAttrs.list}
                        id={Number(id)}
                        isDismissible={isDismissible}
                        name={name}
                        onClickDismissible={() => handleRemove(id)}
                        remotePopover
                        {...chipPropsWithoutColor}
                    />
                );
            });
        }, [
            chipWithLabel,
            data.isTodoDia,
            getChipProps,
            handleRemove,
            inputAttrs.list,
            minSelection,
            readOnly,
            value,
        ]);

        // ========================================
        // RENDER
        // ========================================
        if (shouldRenderField && !shouldRenderField('usersAttendees')) return null;

        const hasError = errors && errors.usersAttendees;

        return (
            <FieldWrapper
                className="fm-users-attendees-field"
                description={description}
                error={hasError}
                isBulkAction={isBulkAction}
                label={label}
                labelMode={labelMode}
                mandatory={mandatory}
            >
                <div className="fm-users-attendees-field__content">
                    <SelectFuzzy
                        autoFocus={firstErrorField}
                        className="fm-users-attendees-field__select"
                        error={!!hasError}
                        feature={AGENDA.entity}
                        field={inputAttrs.nombre}
                        hint={hint}
                        list={inputAttrs.list}
                        onChange={handleChange}
                        overrides={{
                            'react-select': {
                                controlShouldRenderValue: false,
                            },
                        }}
                        readOnly={readOnly}
                        value={null}
                        isFullWidth
                    />
                    {isValidValue && (
                        <div className="fm-users-attendees-field__chips">{renderChips}</div>
                    )}
                    {adviceContent && (
                        <Advice
                            className="fm-users-attendees-field__advice"
                            showCollapse={false}
                            showIcon
                            type={adviceContent.type}
                        >
                            {adviceContent.content}
                        </Advice>
                    )}
                </div>
            </FieldWrapper>
        );
    },
);

UsersAttendeesField.displayName = 'UsersAttendeesField';

UsersAttendeesField.propTypes = propTypes;

export default UsersAttendeesField;
